import React, { useRef } from 'react';
import styled from 'styled-components';
import uuid from 'uuid';
// This component is takent from job-seekers
// users uuid instead of useId

const CODE_LENGTH = 6;

interface Props {
  hasError: boolean;
  onChange: (value: string) => void;
}

const selectInput = (input: React.MutableRefObject<HTMLInputElement | null>) =>
  input?.current?.select();

// With this small timeout, it will actually highlight any text in the input so you can immediately replace it
const selectAndHighlightInput = (
  input: React.MutableRefObject<HTMLInputElement | null>
) => {
  setTimeout(() => selectInput(input), 0);
};

const CodeInput = ({ hasError, onChange }: Props) => {
  const id = uuid.v4();
  const inputRefs = Array.from(new Array(CODE_LENGTH), () =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useRef<HTMLInputElement | null>(null)
  );

  const currentCode = () =>
    inputRefs.map((input) => input.current?.value || '').join('');

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    const currentInput = event.currentTarget as HTMLInputElement;
    const previousInputRef = inputRefs[index - 1];
    const nextInputRef = inputRefs[index + 1];

    if (event.key === 'Backspace' && index > 0 && !currentInput.value) {
      selectInput(previousInputRef);
    } else if (event.key === 'ArrowLeft' && index > 0) {
      selectAndHighlightInput(previousInputRef);
    } else if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      selectAndHighlightInput(nextInputRef);
    }
  };

  const handleChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;

    if (value.length > 1) {
      fillInputs(index, value);
      return;
    }

    onChange(currentCode());

    if (value && index < CODE_LENGTH - 1) {
      selectInput(inputRefs[index + 1]);
    }
  };

  const fillInputs = (startIndex: number, text: string) => {
    const characters = text.split('');

    characters.forEach((character, i) => {
      const inputIndex = startIndex + i;
      if (inputIndex >= CODE_LENGTH) {
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      inputRefs[inputIndex].current!.value = character;
    });

    onChange(currentCode());

    const lastInputIndex = Math.min(
      startIndex + characters.length - 1,
      CODE_LENGTH - 1
    );
    selectInput(inputRefs[lastInputIndex + 1]);
  };

  const handlePaste = (
    startIndex: number,
    event: React.ClipboardEvent<HTMLInputElement>
  ) => {
    event.preventDefault();

    const clipboardData = event.clipboardData?.getData('text') || '';
    fillInputs(startIndex, clipboardData);
  };

  return (
    <fieldset id={id} data-provides="login-security-code">
      <Wrapper>
        {inputRefs.map((inputRef, index) => (
          <Input
            key={index}
            id={`${id}-${index}`}
            data-test-id={`security-input-${index}`}
            type="number"
            ref={inputRefs[index]}
            aria-invalid={hasError}
            aria-errormessage="security-code-error"
            aria-required={true}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange(index, e)
            }
            onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
              handlePaste(index, e)
            }
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
              handleKeyDown(index, e)
            }
            autoFocus={index === 0}
          />
        ))}
      </Wrapper>
    </fieldset>
  );
};

const Wrapper = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const Input = styled.input`
  max-width: 50px;
  height: 60px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  text-align: center;
  box-sizing: border-box;
  font-size: 16px; // prevent auto zoom-in on mobile
`;

export default CodeInput;
